import React from 'react';
import { Box, Typography } from '@mui/material';
import GppMaybeOutlined from '@mui/icons-material/GppMaybeOutlined';

export interface ApprovalCardProps {
  /** What needs a decision, e.g. "SQL approval required". */
  title: string;
  /** Where it will run, e.g. the connector name. */
  source?: string;
  /** One line on what happens if approved. */
  description?: React.ReactNode;
  /** What is being approved: the query, the number to call. */
  children?: React.ReactNode;
  /** The decision buttons. */
  actions?: React.ReactNode;
}

/**
 * Something the agent will not do until a person says yes — run a query,
 * place a call. The warning tone marks that nothing has happened yet.
 */
export function ApprovalCard({ title, source, description, children, actions }: ApprovalCardProps) {
  return (
    <Box
      role="group"
      aria-label={title}
      sx={(t) => ({
        border: `1px solid ${t.tokens.border.default}`,
        borderRadius: `${t.ds.radius.md}px`,
        bgcolor: t.tokens.surface.raised,
        overflow: 'hidden',
      })}
    >
      <Box
        sx={(t) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: t.tokens.semantic.warningSoft,
        })}
      >
        <GppMaybeOutlined fontSize="small" sx={(t) => ({ color: t.tokens.semantic.warning })} aria-hidden />
        <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0 }}>{title}</Typography>
        {source && (
          <Typography variant="caption" noWrap sx={(t) => ({ color: t.tokens.text.secondary })}>
            {source}
          </Typography>
        )}
      </Box>
      <Box sx={{ px: 1.5, py: 1.25, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {description && (
          <Typography variant="body2" component="div" sx={(t) => ({ color: t.tokens.text.secondary })}>
            {description}
          </Typography>
        )}
        {children}
        {actions && <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>{actions}</Box>}
      </Box>
    </Box>
  );
}
